/**
 * @namespace PikkaAgency
 * 
 * @description
 * The `PikkaAgency` namespace is a global container for utility classes and modules used in the application
 * Call the file in the theme's head tag (layout/theme.liquid)
 */
document.addEventListener('DOMContentLoaded', function () {
  (function(global) {
    
    global.PikkaAgency = global.PikkaAgency || {};

    class Utils {
      /**
       * Debounces a function to limit its execution rate.
       * @param {Function} func - The function to debounce.
       * @param {number} delay - The debounce delay in milliseconds.
       * @param {boolean} [immediate=false] - If true, execute the function on the leading edge.
       * @returns {Function} - The debounced function.
       */
      static debounce(func, delay, immediate = false) {
        if (typeof func !== 'function') {
          throw new TypeError('Expected "func" to be a function.');
        }
        
        if (typeof delay !== 'number') {
          throw new TypeError('Expected "delay" to be a number.');
        }
    
        let timer;
        
        return function (...args) {
          const context = this;
          const callNow = immediate && !timer;
    
          clearTimeout(timer);
          timer = setTimeout(() => {
            timer = null;
            if (!immediate) func.apply(context, args);
          }, delay);
    
          if (callNow) func.apply(context, args);
        };
      }
      
      /**
       * Sorts an array of strings alphabetically.
       * @param {Array<string>} items - The array of strings to sort.
       * @returns {Array<string>} - A new array with the strings sorted alphabetically.
       */
      static sortStringsAlphabetically(items) {
        if (!Array.isArray(items)) {
          throw new TypeError(`Expected 'items' to be an array, but got ${typeof items}`);
        }
        
        if (!items.every(item => typeof item === 'string')) {
          throw new TypeError('Expected all elements in the array to be strings.');
        }
      
        return [...items].sort((a, b) => a.localeCompare(b));
      }
      
      /**
       * Filters an array of strings by a given prefix.
       * @param {Array<string>} items - The array of strings to filter.
       * @param {string} prefix - The prefix to match.
       * @returns {Array<string>} - A new array containing items that start with the given prefix.
       */
      static filterStringsByPrefix(items, prefix) {
        if (!Array.isArray(items)) {
          throw new TypeError(`Expected 'items' to be an array, but got ${typeof items}`);
        }
        
        if (typeof prefix !== 'string') {
          throw new TypeError(`Expected 'prefix' to be a string, but got ${typeof prefix}`);
        }
        
        if (!items.every(item => typeof item === 'string')) {
          throw new TypeError('Expected all elements in the array to be strings.');
        }
    
        return items.filter(item => item.startsWith(prefix));
      }

      /**
       * Removes a specified item (e.g., prefix) from the beginning of a string.
       * @param {string} str - The original string value.
       * @param {string} item - The item to remove from the beginning of the string.
       * @returns {string} - The string without the specified item.
       */
      static removeStringPrefix(str, item) {
        if (typeof str !== 'string') {
          throw new TypeError(`Expected 'str' to be a string, but got ${typeof str}`);
        }
        
        if (typeof item !== 'string') {
          throw new TypeError(`Expected 'item' to be a string, but got ${typeof item}`);
        }
        
        return str.startsWith(item) ? str.slice(item.length) : str;
      }

      /**
       * Replaces underscores in a string by spaces.
       * @param {string} str - The string to process.
       * @returns {string} - The string with underscores replaced by spaces.
       */
      static replaceStringUnderscoresBySpaces(str) {
        if (typeof str !== 'string') {
          throw new TypeError('Parameter must be a string.');
        }
        
        return str.replace(/_/g, ' ');
      }

      /**
       * Sets padding for an element based on provided values.
       * Supports one, two, three, or four values:
       * - 1 value: applies to all sides.
       * - 2 values: [vertical, horizontal].
       * - 3 values: [top, horizontal, bottom].
       * - 4 values: [top, right, bottom, left].
       * @param {HTMLElement} element - The element to apply padding to.
       * @param {Array<string>} paddingValues - An array of padding values (1 to 4 values).
       */
      static setElementPadding(element, paddingValues = []) {
        if (!(element instanceof HTMLElement)) {
          throw new TypeError(`Expected 'element' to be an instance of HTMLElement, but got ${element === null ? 'null' : typeof element}`);
        }
    
        if (!Array.isArray(paddingValues)) {
          throw new TypeError(`Expected 'paddingValues' to be an array, but got ${typeof paddingValues}`);
        }
    
        if (!paddingValues.every(value => typeof value === 'string' || typeof value === 'number')) {
          throw new TypeError('All padding values must be strings or numbers.');
        }
      
        // Normalize padding values
        const [top = '', right = '', bottom = '', left = ''] = (() => {
          switch (paddingValues.length) {
            case 1:
              return [paddingValues[0], paddingValues[0], paddingValues[0], paddingValues[0]];
            case 2:
              return [paddingValues[0], paddingValues[1], paddingValues[0], paddingValues[1]];
            case 3:
              return [paddingValues[0], paddingValues[1], paddingValues[2], paddingValues[1]];
            case 4:
              return paddingValues;
            default:
              return ['', '', '', '']; // Default to empty paddings if no valid values provided
          }
        })().map(value => value.trim());
      
        // Apply padding to the element
        element.style.paddingTop = top;
        element.style.paddingRight = right;
        element.style.paddingBottom = bottom;
        element.style.paddingLeft = left;
      }
    }

    /**
    * Singleton manager for global select state and behavior.
    */
    class SelectManager {
      static activeSelect = null;
      static isInteracting = false;
      static interactionTimeout = null;
    
      /**
      * Toggles the select state for the given component.
      * If another select is open, it closes that select first.
      * @param {PkSelect} select - The PkSelect component to toggle.
      */
      static toggleSelect(select) {
        if (this.activeSelect && this.activeSelect !== select) {
          this.activeSelect.closeSelect();
        }
    
        if (this.activeSelect === select) {
          this.clearActiveSelect();
        } else {
          this.setActiveSelect(select);
        }
      }
    
      /**
      * Sets the given select as the active select and opens it.
      * @param {PkSelect} select - The PkSelect component to set as active.
      */
      static setActiveSelect(select) {
        this.activeSelect = select;
        this.isInteracting = true;
        select.openSelect();
      }
    
      /**
      * Clears the active select and closes it.
      */
      static clearActiveSelect() {
        if (this.activeSelect) {
          this.activeSelect.closeSelect();
          this.activeSelect = null;
          this.isInteracting = false;
        }
  
        clearTimeout(this.interactionTimeout);
        this.interactionTimeout = setTimeout(() => {
          this.isInteracting = false;
        }, 300);
      }
    
      /**
      * Handles global click events to close the active select if the click happens outside.
      * @param {MouseEvent} e - The click event.
      */
      static handleGlobalClick(e) {
        if (this.activeSelect && !this.activeSelect.contains(e.target)) {
          this.clearActiveSelect();
        }
      }
    }

    /**
     * A utility class for rate-limiting concurrent fetch operations.
     * Dynamically adjusts concurrency based on network latency.
     */
    class RateLimitedFetcher {
      constructor(concurrency = 3, adjustInterval = 5000) {
        this.concurrency = concurrency; // @type {number} The current concurrency level.
        this.taskQueue = []; // @type {Array<Object>} Queue of tasks to be executed.
        this.activeTasks = 0; // @type {number} The number of tasks currently being executed.
        this.adjustInterval = adjustInterval; // @type {number} The interval (in milliseconds) for adjusting concurrency.
        this.latencyHistory = []; // @type {Array<number>} Array storing the latencies of recent tasks.
        this.MAX_CONCURRENCY = 10; // @type {number} The maximum allowable concurrency level.
        this.MIN_CONCURRENCY = 1; // @type {number} The minimum allowable concurrency level.
    
        // Store the interval ID for later clearing
        this.adjustIntervalId = setInterval(() => this.adjustConcurrency(), this.adjustInterval);
      }
    
      // Add a fetch task to the queue
      enqueue(task) {
        return new Promise((resolve, reject) => {
          this.taskQueue.push({ task, resolve, reject });
          this.runNext();
        });
      }
    
      // Run the next task in the queue if concurrency allows
      runNext() {
        if (this.activeTasks < this.concurrency && this.taskQueue.length > 0) {
          const { task, resolve, reject } = this.taskQueue.shift();
          this.activeTasks++;
    
          const startTime = Date.now();
    
          task()
            .then(result => {
              this.recordLatency(Date.now() - startTime); // Track latency
              resolve(result);
            })
            .catch(error => reject(error))
            .finally(() => {
              this.activeTasks--;
              this.runNext();
    
              // Stop adjusting concurrency if all tasks are done
              if (this.activeTasks === 0 && this.taskQueue.length === 0) {
                this.stopAdjusting();
              }
            });
        }
      }
    
      // Stop the interval for adjusting concurrency
      stopAdjusting() {
        if (this.adjustIntervalId) {
          clearInterval(this.adjustIntervalId);
          this.adjustIntervalId = null;
        }
      }
    
      /**
       * Records the latency of a task for adjusting concurrency.
       * @param {number} latency - The time (in ms) it took for the task to complete.
       */
      recordLatency(latency) {
        this.latencyHistory.push(latency);
        if (this.latencyHistory.length > 10) {
          this.latencyHistory.shift();
        }
      }
    
      // Dynamically adjust concurrency
      adjustConcurrency() {
        const averageLatency =
          this.latencyHistory.reduce((sum, time) => sum + time, 0) /
          (this.latencyHistory.length || 1);
    
        if (averageLatency > 1000) {
          this.concurrency = Math.max(this.MIN_CONCURRENCY, this.concurrency - 1);
        } else if (averageLatency < 500 && this.concurrency < this.MAX_CONCURRENCY) {
          this.concurrency++;
        }    
      }
    }

    global.PikkaAgency.Utils = PikkaAgency.Utils || Utils;
    global.PikkaAgency.SelectManager = PikkaAgency.SelectManager || SelectManager;
    global.PikkaAgency.RateLimitedFetcher = global.PikkaAgency.RateLimitedFetcher || RateLimitedFetcher;

    global.document.addEventListener('click', (e) => global.PikkaAgency.SelectManager.handleGlobalClick(e));
  })(window);
});